import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase-client';
import { useAuth } from '../../../auth/AuthContext';
import SubmitButton from '../formComponents/SubmitButton';
import {
  getAdminPermissionGroupsForModules,
  normalizePermissionKeys,
} from '../../teams/utils/adminPermissions';

const GLOBAL_ROLE_ID = 'global-admin';
const NEW_ROLE_ID = 'new-role';
const NO_ROLE_VALUE = '';
const EMPTY_ROLE = {
  id: '',
  name: '',
  description: '',
  permissionKeys: [],
};

function normalizeRoleDraftPermissionKeys(role = {}) {
  const keys = normalizePermissionKeys(role.permissionKeys);
  const next = new Set(keys);
  const schemaVersion = Number(role.permissionSchemaVersion || 0);
  if (schemaVersion < 2 && next.has('sailingFleet.manage')) {
    next.add('sailingBoatBooking.manage');
    next.add('sailingOutings.manage');
  }
  if (schemaVersion < 3 && next.has('rowing.manage')) {
    next.add('rowingFleet.manage');
    next.add('rowingBoatBooking.manage');
    next.add('rowingOutings.manage');
    next.add('rowingBoatMaintenance.manage');
  }
  if (schemaVersion < 3 && next.has('sailingFleet.manage')) {
    next.add('sailingBoatMaintenance.manage');
  }
  return normalizePermissionKeys(Array.from(next));
}

function memberName(member = {}) {
  return member.name || member.displayName || member.email || 'Member';
}

function normalizeSearch(value) {
  return String(value || '').trim().toLowerCase();
}

export default function EditAdminPermissionsForm({
  isOpen,
  teamId,
  roles = [],
  assignments = [],
  members = [],
  fullAdminIds = [],
  modules = {},
  metadata = {},
  onClose,
  onSave,
}) {
  const { user } = useAuth();
  const [mode, setMode] = useState('roles');
  const [selectedRoleId, setSelectedRoleId] = useState(GLOBAL_ROLE_ID);
  const [draft, setDraft] = useState(EMPTY_ROLE);
  const [memberSearch, setMemberSearch] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [deletingRole, setDeletingRole] = useState(false);
  const [savingMemberUid, setSavingMemberUid] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [roleSubmitAttempted, setRoleSubmitAttempted] = useState(false);

  const fullAdminSet = useMemo(() => new Set((fullAdminIds || []).filter(Boolean)), [fullAdminIds]);
  const membersById = useMemo(() => new Map(members.map((member) => [member.id, member])), [members]);
  const assignmentsByUid = useMemo(() => {
    const map = new Map();
    assignments.forEach((assignment) => {
      const uid = assignment?.uid || assignment?.id;
      if (uid) map.set(uid, assignment);
    });
    return map;
  }, [assignments]);

  const sortedMembers = useMemo(() => (
    [...members]
      .filter((member) => member?.id)
      .sort((left, right) => memberName(left).localeCompare(memberName(right)))
  ), [members]);

  const selectedRole = useMemo(
    () => roles.find((role) => role.id === selectedRoleId) || null,
    [roles, selectedRoleId]
  );
  const selectedIsGlobal = selectedRoleId === GLOBAL_ROLE_ID;
  const selectedIsNew = selectedRoleId === NEW_ROLE_ID;
  const selectedIsCustom = !selectedIsGlobal && !selectedIsNew && Boolean(selectedRole);
  const permissionGroups = useMemo(
    () => getAdminPermissionGroupsForModules(modules, { includeKeys: draft.permissionKeys, metadata }),
    [draft.permissionKeys, metadata, modules]
  );

  const fullAdminMembers = useMemo(() => (
    Array.from(fullAdminSet)
      .map((uid) => membersById.get(uid) || { id: uid, name: uid })
      .sort((left, right) => memberName(left).localeCompare(memberName(right)))
  ), [fullAdminSet, membersById]);

  const selectedRoleAssignmentCount = useMemo(() => {
    if (!selectedIsCustom) return 0;
    return assignments.filter((assignment) => assignment.roleId === selectedRoleId).length;
  }, [assignments, selectedIsCustom, selectedRoleId]);

  const filteredMembers = useMemo(() => {
    const needle = normalizeSearch(memberSearch);
    return sortedMembers.filter((member) => {
      if (!needle) return true;
      return memberName(member).toLowerCase().includes(needle)
        || String(member.email || '').toLowerCase().includes(needle);
    });
  }, [memberSearch, sortedMembers]);

  const globalAssignableMembers = useMemo(() => {
    const needle = normalizeSearch(globalSearch);
    return sortedMembers
      .filter((member) => {
        if (fullAdminSet.has(member.id)) return false;
        if (!needle) return true;
        return memberName(member).toLowerCase().includes(needle)
          || String(member.email || '').toLowerCase().includes(needle);
      })
      .slice(0, 8);
  }, [fullAdminSet, globalSearch, sortedMembers]);

  useEffect(() => {
    if (!isOpen) return;
    if (selectedIsGlobal || selectedIsNew) {
      setDraft(EMPTY_ROLE);
    } else if (selectedRole) {
      setDraft({
        id: selectedRole.id,
        name: selectedRole.name || '',
        description: selectedRole.description || '',
        permissionKeys: normalizeRoleDraftPermissionKeys(selectedRole),
      });
    } else {
      setSelectedRoleId(GLOBAL_ROLE_ID);
      setDraft(EMPTY_ROLE);
    }
    setError('');
  }, [isOpen, selectedIsGlobal, selectedIsNew, selectedRole]);

  if (!isOpen) return null;

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError('');
    setSuccessMessage('');
    setRoleSubmitAttempted(false);
  };

  const selectRole = (roleId) => {
    setSelectedRoleId(roleId);
    setError('');
    setSuccessMessage('');
    setRoleSubmitAttempted(false);
  };

  const handleNewRole = () => {
    setMode('roles');
    selectRole(NEW_ROLE_ID);
  };

  const togglePermission = (permissionKey) => {
    setSuccessMessage('');
    setDraft((current) => {
      const keys = new Set(current.permissionKeys || []);
      if (keys.has(permissionKey)) keys.delete(permissionKey);
      else keys.add(permissionKey);
      return {
        ...current,
        permissionKeys: normalizePermissionKeys(Array.from(keys)),
      };
    });
  };

  const callAssignRole = async (memberId, roleId) => {
    const assignAdminRole = httpsCallable(functions, 'assignAdminRole');
    await assignAdminRole({ teamId, memberId, roleId });
  };

  const callSetGlobalAdmin = async (memberId, isAdmin) => {
    const setAdminFn = httpsCallable(functions, 'setTeamMemberAdminStatus');
    await setAdminFn({ teamId, memberId, isAdmin });
  };

  const handleSaveRole = async (event) => {
    event.preventDefault();
    const name = String(draft.name || '').trim();
    const permissionKeys = normalizePermissionKeys(draft.permissionKeys);
    setRoleSubmitAttempted(true);
    setSuccessMessage('');
    setError('');

    if (!name) {
      setError('Add a role name before saving.');
      return;
    }
    if (permissionKeys.length === 0) {
      setError('Choose at least one access area before saving this role.');
      return;
    }

    setSavingRole(true);
    try {
      const saveAdminRole = httpsCallable(functions, 'saveAdminRole');
      const result = await saveAdminRole({
        teamId,
        roleId: draft.id || undefined,
        name,
        description: draft.description,
        permissionKeys,
      });
      const nextRoleId = result.data?.roleId || draft.id || '';
      if (nextRoleId) {
        setSelectedRoleId(nextRoleId);
        setDraft((current) => ({ ...current, id: nextRoleId }));
      }
      await onSave?.();
      setRoleSubmitAttempted(false);
      setSuccessMessage('Role saved.');
    } catch (err) {
      setError(err?.message || 'Failed to save role.');
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!draft.id) return;
    const confirmed = window.confirm(`Delete ${draft.name || 'this role'} and remove it from assigned members?`);
    if (!confirmed) return;
    setDeletingRole(true);
    setError('');
    try {
      const deleteAdminRole = httpsCallable(functions, 'deleteAdminRole');
      await deleteAdminRole({ teamId, roleId: draft.id });
      selectRole(GLOBAL_ROLE_ID);
      await onSave?.();
    } catch (err) {
      setError(err?.message || 'Failed to delete role.');
    } finally {
      setDeletingRole(false);
    }
  };

  const handleMemberRoleChange = async (memberId, nextRoleId, skipConfirmation = false) => {
    if (!memberId) return;
    const currentlyGlobal = fullAdminSet.has(memberId);
    const currentAssignment = assignmentsByUid.get(memberId);
    const currentRoleId = currentlyGlobal ? GLOBAL_ROLE_ID : currentAssignment?.roleId || NO_ROLE_VALUE;
    if (nextRoleId === currentRoleId) return;
    if (!skipConfirmation && currentRoleId) {
      const member = membersById.get(memberId) || { id: memberId };
      const nextRoleName = nextRoleId === GLOBAL_ROLE_ID
        ? 'Global Admin'
        : roles.find((role) => role.id === nextRoleId)?.name || 'no admin access';
      if (!window.confirm(`Change ${memberName(member)} from ${currentlyGlobal ? 'Global Admin' : currentAssignment?.roleName || 'their current admin role'} to ${nextRoleName}?`)) return;
    }

    setSavingMemberUid(memberId);
    setError('');
    try {
      if (nextRoleId === GLOBAL_ROLE_ID) {
        await callSetGlobalAdmin(memberId, true);
      } else if (currentlyGlobal) {
        await callSetGlobalAdmin(memberId, false);
        if (nextRoleId) await callAssignRole(memberId, nextRoleId);
      } else {
        await callAssignRole(memberId, nextRoleId);
      }
      await onSave?.();
    } catch (err) {
      setError(err?.message || 'Failed to update admin role.');
    } finally {
      setSavingMemberUid('');
    }
  };

  const handleAddGlobalAdmin = async (memberId) => {
    if (!memberId) return;
    setSavingMemberUid(memberId);
    setError('');
    try {
      await callSetGlobalAdmin(memberId, true);
      setGlobalSearch('');
      await onSave?.();
    } catch (err) {
      setError(err?.message || 'Failed to add global admin.');
    } finally {
      setSavingMemberUid('');
    }
  };

  const handleRemoveGlobalAdmin = async (memberId) => {
    if (!memberId) return;
    const member = membersById.get(memberId) || { id: memberId, name: 'Member' };
    const confirmed = window.confirm(`Remove Global Admin access from ${memberName(member)}?`);
    if (!confirmed) return;
    await handleMemberRoleChange(memberId, NO_ROLE_VALUE, true);
  };

  const roleValueForMember = (memberId) => {
    if (fullAdminSet.has(memberId)) return GLOBAL_ROLE_ID;
    return assignmentsByUid.get(memberId)?.roleId || NO_ROLE_VALUE;
  };

  const renderRoleEditor = () => (
    <div className="admin-permissions-workspace__grid">
      <aside className="admin-permissions-workspace__roles" aria-label="Admin roles">
        <button
          type="button"
          className={`admin-permissions-workspace__role ${selectedIsGlobal ? 'is-selected' : ''}`}
          onClick={() => selectRole(GLOBAL_ROLE_ID)}
        >
          <span>
            <strong>Global Admin</strong>
            <small>{fullAdminSet.size} assigned</small>
          </span>
          <em>Locked</em>
        </button>

        <div className="admin-permissions-workspace__role-heading">Limited roles</div>

        {roles.length === 0 ? (
          <p className="text-muted admin-permissions-workspace__empty">No limited roles yet.</p>
        ) : roles.map((role) => {
          const assignmentCount = assignments.filter((assignment) => assignment.roleId === role.id).length;
          return (
            <button
              key={role.id}
              type="button"
              className={`admin-permissions-workspace__role ${selectedRoleId === role.id ? 'is-selected' : ''}`}
              onClick={() => selectRole(role.id)}
            >
              <span>
                <strong>{role.name || 'Unnamed role'}</strong>
                <small>{assignmentCount} assigned</small>
              </span>
            </button>
          );
        })}

        {selectedIsNew && (
          <button type="button" className="admin-permissions-workspace__role is-selected">
            <span>
              <strong>New role</strong>
              <small>Unsaved</small>
            </span>
          </button>
        )}
      </aside>

      <section className="admin-permissions-workspace__detail">
        {selectedIsGlobal ? (
          <>
            <div className="admin-permissions-workspace__detail-header">
              <div>
                <h3>Global Admin</h3>
                <p className="text-muted">Full access to every club setting and action.</p>
              </div>
              <span className="admin-permissions-workspace__badge">Locked</span>
            </div>

            <div className="admin-permissions-workspace__notice">
              Global Admin is a built-in role. Use the Global Admins tab to add or remove members.
            </div>

            <div className="admin-permissions-workspace__summary-row">
              <span>{fullAdminSet.size} members assigned</span>
              <button type="button" className="btn btn--secondary btn--xs" onClick={() => switchMode('global')}>
                Manage Global Admins
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSaveRole}>
            <div className="admin-permissions-workspace__detail-header">
              <div>
                <h3>{selectedIsNew ? 'New limited role' : draft.name || 'Unnamed role'}</h3>
                <p className="text-muted">Choose the areas this role can manage.</p>
              </div>
              {draft.id && (
                <button
                  type="button"
                  className="btn btn--danger btn--xs"
                  onClick={handleDeleteRole}
                  disabled={deletingRole || savingRole}
                >
                  {deletingRole ? 'Deleting...' : 'Delete'}
                </button>
              )}
            </div>

            {selectedIsCustom && (
              <div className="admin-permissions-workspace__summary-row">
                <span>{selectedRoleAssignmentCount} members assigned</span>
                <button type="button" className="btn btn--secondary btn--xs" onClick={() => switchMode('assign')}>
                  Assign Members
                </button>
              </div>
            )}

            <div className="admin-permissions-workspace__fields">
              <label>
                <span className="form-label">Role name</span>
                <input
                  className="form-input w-full"
                  value={draft.name}
                  onChange={(event) => {
                    setSuccessMessage('');
                    setDraft((current) => ({ ...current, name: event.target.value }));
                  }}
                  aria-invalid={roleSubmitAttempted && !draft.name.trim()}
                  required
                />
                {roleSubmitAttempted && !draft.name.trim() && (
                  <span className="admin-permissions-workspace__field-error">Add a role name.</span>
                )}
              </label>

              <label>
                <span className="form-label">Description</span>
                <textarea
                  className="form-input w-full"
                  value={draft.description}
                  rows={2}
                  onChange={(event) => {
                    setSuccessMessage('');
                    setDraft((current) => ({ ...current, description: event.target.value }));
                  }}
                />
              </label>
            </div>

            <div className="admin-permissions-workspace__section-heading">
              <h4>Access areas</h4>
              <span>{(draft.permissionKeys || []).length} selected</span>
            </div>
            <div className="admin-permissions-workspace__permission-grid">
              {permissionGroups.map((group) => (
                <label
                  key={group.key}
                  className={`admin-permissions-workspace__permission ${group.available ? '' : 'admin-permissions-workspace__permission--unavailable'}`.trim()}
                >
                  <input
                    type="checkbox"
                    checked={(draft.permissionKeys || []).includes(group.key)}
                    onChange={() => togglePermission(group.key)}
                  />
                  <span>
                    <strong>{group.label}</strong>
                    <small>{group.description}</small>
                    {!group.available && <small className="admin-permissions-workspace__permission-note">Module not currently available</small>}
                  </span>
                </label>
              ))}
            </div>
            {roleSubmitAttempted && (draft.permissionKeys || []).length === 0 && (
              <div className="admin-permissions-workspace__field-error admin-permissions-workspace__field-error--block">
                Choose at least one access area for this role.
              </div>
            )}

            <div className="form-actions admin-permissions-workspace__actions">
              <SubmitButton
                type="submit"
                label={savingRole ? 'Saving...' : 'Save role'}
                variant="primary"
                disabled={savingRole || deletingRole}
              />
            </div>
          </form>
        )}
      </section>
    </div>
  );

  const renderAssignRoles = () => (
    <section className="admin-permissions-workspace__detail admin-permissions-workspace__detail--full">
      <div className="admin-permissions-workspace__detail-header">
        <div>
          <h3>Assign Roles</h3>
          <p className="text-muted">Choose the admin access level for each member.</p>
        </div>
      </div>

      <label className="admin-permissions-workspace__search">
        <span className="form-label">Search members</span>
        <input
          className="form-input w-full"
          value={memberSearch}
          onChange={(event) => setMemberSearch(event.target.value)}
          placeholder="Search by name or email"
        />
      </label>

      <div className="admin-permissions-workspace__assignment-table">
        {filteredMembers.length === 0 ? (
          <p className="text-muted">No members match that search.</p>
        ) : filteredMembers.map((member) => {
          const roleValue = roleValueForMember(member.id);
          const saving = savingMemberUid === member.id;
          const disableSelfDemotion = member.id === user?.uid && roleValue === GLOBAL_ROLE_ID;
          const disableLastGlobal = roleValue === GLOBAL_ROLE_ID && fullAdminSet.size <= 1;
          return (
            <div key={member.id} className="admin-permissions-workspace__assignment-row">
              <div>
                <strong>{memberName(member)}</strong>
                <span className="text-muted">
                  {roleValue === GLOBAL_ROLE_ID
                    ? 'Full access'
                    : assignmentsByUid.get(member.id)?.roleName || 'No admin access'}
                </span>
              </div>
              <select
                className="form-input"
                value={roleValue}
                disabled={saving || disableSelfDemotion || disableLastGlobal}
                onChange={(event) => handleMemberRoleChange(member.id, event.target.value)}
                aria-label={`Admin role for ${memberName(member)}`}
                title={disableSelfDemotion ? 'You cannot change your own Global Admin access' : disableLastGlobal ? 'At least one Global Admin is required' : undefined}
              >
                <option value="">No admin role</option>
                <option value={GLOBAL_ROLE_ID}>Global Admin</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>{role.name || 'Unnamed role'}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </section>
  );

  const renderGlobalAdmins = () => (
    <section className="admin-permissions-workspace__detail admin-permissions-workspace__detail--full">
      <div className="admin-permissions-workspace__detail-header">
        <div>
          <h3>Global Admins</h3>
          <p className="text-muted">Global Admins have full access to every club setting and action.</p>
        </div>
      </div>

      <div className="admin-permissions-workspace__member-list">
        {fullAdminMembers.map((member) => {
          const disableSelf = member.id === user?.uid;
          const disableLast = fullAdminSet.size <= 1;
          const saving = savingMemberUid === member.id;
          return (
            <div key={member.id} className="admin-permissions-workspace__member-row">
              <span>{memberName(member)}</span>
              <button
                type="button"
                className="btn btn--secondary btn--xs"
                disabled={saving || disableSelf || disableLast}
                onClick={() => handleRemoveGlobalAdmin(member.id)}
                title={disableSelf ? 'You cannot remove your own Global Admin access' : disableLast ? 'At least one Global Admin is required' : 'Remove Global Admin'}
              >
                {saving ? 'Removing...' : 'Remove'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="admin-permissions-workspace__member-search">
        <label>
          <span className="form-label">Add Global Admin</span>
          <input
            className="form-input w-full"
            value={globalSearch}
            onChange={(event) => setGlobalSearch(event.target.value)}
            placeholder="Search members"
          />
        </label>
        <div className="admin-permissions-workspace__member-results">
          {globalAssignableMembers.length === 0 ? (
            <p className="text-muted">No matching members available.</p>
          ) : globalAssignableMembers.map((member) => (
            <button
              key={member.id}
              type="button"
              className="admin-permissions-workspace__member-result"
              disabled={savingMemberUid === member.id}
              onClick={() => handleAddGlobalAdmin(member.id)}
            >
              <span>
                <strong>{memberName(member)}</strong>
                {assignmentsByUid.get(member.id)?.roleName && <small>Currently {assignmentsByUid.get(member.id).roleName}</small>}
              </span>
              <em>{savingMemberUid === member.id ? 'Adding...' : 'Add'}</em>
            </button>
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <div className="modal-form admin-permissions-workspace">
      <div className="admin-permissions-workspace__header">
        <div>
          <h2>Admin Permissions</h2>
          <p className="text-muted">Create roles, assign members, and manage full admin access.</p>
        </div>
        <button type="button" className="btn btn--secondary btn--sm" onClick={handleNewRole}>
          New role
        </button>
      </div>

      <div className="admin-permissions-workspace__tabs" role="tablist" aria-label="Admin permission management">
        <button type="button" role="tab" aria-selected={mode === 'roles'} className={`admin-permissions-workspace__tab ${mode === 'roles' ? 'is-selected' : ''}`} onClick={() => switchMode('roles')}>
          Roles
        </button>
        <button type="button" role="tab" aria-selected={mode === 'assign'} className={`admin-permissions-workspace__tab ${mode === 'assign' ? 'is-selected' : ''}`} onClick={() => switchMode('assign')}>
          Assign Roles
        </button>
        <button type="button" role="tab" aria-selected={mode === 'global'} className={`admin-permissions-workspace__tab ${mode === 'global' ? 'is-selected' : ''}`} onClick={() => switchMode('global')}>
          Global Admins
        </button>
      </div>

      {error && <div className="modal-error" role="alert">{error}</div>}
      {successMessage && <div className="admin-permissions-workspace__success" role="status">{successMessage}</div>}

      {mode === 'roles' && renderRoleEditor()}
      {mode === 'assign' && renderAssignRoles()}
      {mode === 'global' && renderGlobalAdmins()}

      <div className="form-actions" style={{ marginTop: 16 }}>
        <SubmitButton type="button" label="Close" variant="ghost" size="sm" onClick={onClose} />
      </div>
    </div>
  );
}
